import { memo } from 'react';
import { Box } from '@mui/material';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import { BasicRow, MediaItem, ShowImagePreview } from '@/entities/table';

export const Thumbnail = memo(function Thumbnail({
  src,
  onPreview,
}: {
  src: string;
  onPreview: ShowImagePreview;
}) {
  const isVideo = /\.(mp4|webm|ogg)$/i.test(src);
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.100',
        width: 80,
        height: 50,
        cursor: 'pointer',
      }}
      onClick={() => onPreview(src)}
    >
      {isVideo ? (
        <video
          src={src}
          muted
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <img
          src={src}
          alt="thumbnail"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      {isVideo && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 4,
            right: 4,
            bgcolor: 'rgba(0,0,0,0.6)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
          }}
        >
          <PlayCircleFilledWhiteIcon sx={{ fontSize: 16, color: '#fff' }} />
        </Box>
      )}
    </Box>
  );
});

export const ThumbnailGroup = memo(function ThumbnailGroup({
  srcArr,
  row,
  onPreview,
}: {
  srcArr: MediaItem[] | MediaItem;
  row: BasicRow;
  onPreview: ShowImagePreview;
}) {
  // Guard against undefined/null and empty arrays
  if (!srcArr || (Array.isArray(srcArr) && srcArr.length === 0)) {
    return null;
  }

  if (!Array.isArray(srcArr)) {
    const source =
      typeof srcArr === 'string'
        ? srcArr
        : srcArr && 'src' in srcArr
          ? (srcArr as { src?: string }).src
          : '';

    if (!source) return null;

    return <Thumbnail src={source} onPreview={onPreview} />;
  }

  const maxVisible = 3;
  const items = (srcArr as MediaItem[]).filter(Boolean) as MediaItem[];
  const extraCount = Math.max(0, items.length - maxVisible);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
      {items.slice(0, maxVisible).map((item, idx: number) => {
        const source: string =
          typeof item === 'string'
            ? item
            : (item as { src?: string })?.src || '';
        if (!source) return null;
        const isVideo = /\.(mp4|webm|ogg)$/i.test(source);

        return (
          <Box
            key={idx}
            sx={{
              width: 60,
              height: 40,
              borderRadius: 1,
              overflow: 'hidden',
              border: '2px solid #fff',
              marginLeft: idx === 0 ? 0 : '-15px',
              cursor: 'pointer',
              position: 'relative',
              boxShadow: 2,
              zIndex: maxVisible - idx,
              transition: 'transform 0.2s ease, z-index 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                zIndex: 10,
                boxShadow: 3,
              },
            }}
            onClick={() => onPreview(row)}
          >
            {isVideo ? (
              <video
                src={source}
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            ) : (
              <img
                src={source}
                alt={`thumbnail-${idx}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            )}
          </Box>
        );
      })}

      {extraCount > 0 && (
        <Box
          sx={{
            width: 60,
            height: 40,
            borderRadius: 1,
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '0.875rem',
            fontWeight: 600,
            marginLeft: '-15px',
            border: '2px solid #fff',
            boxShadow: 2,
            position: 'relative',
            zIndex: 1,
            cursor: 'pointer',
            transition: 'transform 0.2s ease',
            '&:hover': { transform: 'translateY(-2px)', zIndex: 10 },
          }}
          onClick={() => {}}
        >
          +{extraCount}
        </Box>
      )}
    </Box>
  );
});
